import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const PayrollAnalytics = ({ data, meta }) => {
  if (!data) return null;

  // Salary Distribution Chart
  const salaryDistributionData = {
    labels: data.salaryDistribution?.map(item => item.range) || [],
    datasets: [
      {
        label: 'Employee Count',
        data: data.salaryDistribution?.map(item => item.count) || [],
        backgroundColor: [
          '#4caf50',
          '#2196f3',
          '#ff9800',
          '#f44336',
          '#9c27b0',
          '#ff5722',
          '#795548'
        ],
        borderWidth: 1,
      },
    ],
  };

  // Cost Trend Chart
  const costTrendData = {
    labels: data.costTrend?.map(item => item.month) || [],
    datasets: [
      {
        label: 'Gross Salary',
        data: data.costTrend?.map(item => item.grossSalary) || [],
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#1976d2',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
      {
        label: 'Net Salary',
        data: data.costTrend?.map(item => item.netSalary) || [],
        borderColor: '#2e7d32',
        backgroundColor: 'rgba(46, 125, 50, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#2e7d32',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
      {
        label: 'Deductions',
        data: data.costTrend?.map(item => item.deductions) || [],
        borderColor: '#f57c00',
        backgroundColor: 'rgba(245, 124, 0, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#f57c00',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  // Deduction Types Breakdown
  const deductionLabels = [];
  const deductionValues = [];

  if (data.deductions) {
    if (data.deductions.pf > 0) {
      deductionLabels.push('PF');
      deductionValues.push(data.deductions.pf);
    }
    if (data.deductions.esi > 0) {
      deductionLabels.push('ESI');
      deductionValues.push(data.deductions.esi);
    }
    if (data.deductions.professionalTax > 0) {
      deductionLabels.push('Professional Tax');
      deductionValues.push(data.deductions.professionalTax);
    }
    if (data.deductions.tds > 0) {
      deductionLabels.push('TDS');
      deductionValues.push(data.deductions.tds);
    }
    if (data.deductions.loan > 0) {
      deductionLabels.push('Loan');
      deductionValues.push(data.deductions.loan);
    }
    if (data.deductions.other > 0) {
      deductionLabels.push('Other');
      deductionValues.push(data.deductions.other);
    }
  }

  const deductionData = {
    labels: deductionLabels,
    datasets: [
      {
        data: deductionValues,
        backgroundColor: [
          '#1976d2',
          '#2e7d32',
          '#f57c00',
          '#9c27b0',
          '#d32f2f',
          '#7b1fa2'
        ],
        hoverBackgroundColor: [
          '#1565c0',
          '#1b5e20',
          '#ef6c00',
          '#7b1fa2',
          '#b71c1c',
          '#4a148c'
        ],
      }
    ]
  };

  // Department-wise Costs
  const departmentCostData = {
    labels: data.departmentWiseCosts?.map(item => item.department) || [],
    datasets: [
      {
        label: 'Total Salary Cost',
        data: data.departmentWiseCosts?.map(item => item.totalSalary) || [],
        backgroundColor: 'rgba(25, 118, 210, 0.8)',
        borderColor: '#1976d2',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + value.toLocaleString();
          }
        }
      },
    },
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return '₹' + context.parsed.toLocaleString();
          }
        }
      }
    },
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': ₹' + context.parsed.y.toLocaleString();
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + value.toLocaleString();
          }
        }
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Payroll Analytics Overview
      </Typography>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  ₹{data.keyMetrics?.totalPayrollCost?.toLocaleString() || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Total Payroll Cost</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e8', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon sx={{ color: '#2e7d32', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  ₹{Math.round(data.keyMetrics?.avgSalary || 0).toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2">Average Salary</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountIcon sx={{ color: '#f57c00', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                  ₹{data.keyMetrics?.totalDeductions?.toLocaleString() || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Total Deductions</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#9c27b0', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 'bold' }}>
                  {data.thisMonthSummary?.employeeCount || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Employees Paid</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Salary Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Salary Distribution
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300 }}>
                <Bar
                  data={salaryDistributionData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Deduction Breakdown */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <AccountIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Deduction Breakdown ({meta?.period?.month || 'Current Month'})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                {deductionValues.length > 0 ? (
                  <Doughnut data={deductionData} options={pieOptions} />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    No deduction data available
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Cost Trend */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Payroll Cost Trend (Last 12 Months)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300 }}>
                <Line
                  data={costTrendData}
                  options={lineOptions}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Department-wise Costs */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Department-wise Salary Costs ({meta?.period?.month || 'Current Month'})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300 }}>
                <Bar
                  data={departmentCostData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Insights */}
      {data.thisMonthSummary && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payroll Summary ({meta?.period?.month || 'Current Month'})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  textAlign: 'center',
                  bgcolor: '#e3f2fd'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    ₹{data.thisMonthSummary.totalGross?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gross Salary
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  textAlign: 'center',
                  bgcolor: '#fff3e0'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#f57c00' }}>
                    ₹{data.thisMonthSummary.totalDeductions?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Deductions
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  textAlign: 'center',
                  bgcolor: '#e8f5e8'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    ₹{data.thisMonthSummary.totalNet?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net Salary
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PayrollAnalytics;
